import { __rest } from 'tslib';
import React from 'react';
import AntModal from 'antd-core/es/modal';
import { Button } from '../Button/Button.js';
import { Close } from '../../icons/Close/Close.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { useTranslation } from '../../locale/useTranslation.js';
import { Styles, Box, ImageContainer, Img } from './styled.js';

const CloseIcon = (props) => {
    const t = useTranslation();
    const isEmpty = Object.entries(props).length === 0;
    const tooltipProps = React.useMemo(() => {
        if (isEmpty)
            return { title: t('general.close') };
        return props;
    }, [isEmpty, props, t]);
    return (React.createElement(Tooltip, Object.assign({}, tooltipProps),
        React.createElement(Close, null)));
};
const Footer = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const [loading, setLoading] = React.useState(false);
    const handleCancelClick = React.useCallback(() => {
        var _a;
        const onCancelReturnValue = (_a = props.onCancel) === null || _a === void 0 ? void 0 : _a.call(props);
        if (onCancelReturnValue instanceof Promise === false) {
            props.destroy();
            return;
        }
        setLoading(true);
        onCancelReturnValue
            .then(() => {
            props.destroy();
        })
            .catch(() => {
            console.log('[Dialog]: Error');
        })
            .finally(() => {
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onCancel, props.destroy]);
    const handleOkClick = React.useCallback(() => {
        var _a;
        if (!props.onOk) {
            props.destroy();
            return;
        }
        const onOkReturnValue = (_a = props.onOk) === null || _a === void 0 ? void 0 : _a.call(props);
        if (onOkReturnValue instanceof Promise === false) {
            props.destroy();
            return;
        }
        setLoading(true);
        onOkReturnValue
            .then(() => {
            props.destroy();
        })
            .catch(() => {
            console.log('[Dialog]: Error');
        })
            .finally(() => {
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onOk, props.destroy]);
    if (typeof props.footer === 'undefined')
        return (React.createElement(Box, { "$display": 'flex', "$justifyContent": 'flex-end', "$alignItems": 'center', "$gap": 12, "$margin": '24px 0 0', "$padding": props.padding },
            React.createElement(Button, Object.assign({ "data-testid": 'tend-ui-dialog-cancel-button', variant: 'secondary', loading: loading }, props.cancelButtonProps, { onClick: (_b = (_a = props.cancelButtonProps) === null || _a === void 0 ? void 0 : _a.onClick) !== null && _b !== void 0 ? _b : handleCancelClick }), ((_c = props.cancelButtonProps) === null || _c === void 0 ? void 0 : _c.children) || props.cancelText),
            React.createElement(Button, Object.assign({ "data-testid": 'tend-ui-dialog-ok-button', loading: loading }, props.okButtonProps, { onClick: (_e = (_d = props.okButtonProps) === null || _d === void 0 ? void 0 : _d.onClick) !== null && _e !== void 0 ? _e : handleOkClick }), ((_f = props.okButtonProps) === null || _f === void 0 ? void 0 : _f.children) || props.okText)));
    if (Array.isArray(props.footer))
        return (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$justifyContent": 'flex-end', "$gap": 12, "$margin": '24px 0 0', "$padding": props.padding }, props.footer.map(node => node)));
    return (React.createElement(Box, { "$margin": '24px 0 0', "$padding": props.padding }, props.footer));
};
const methodsFactory = (modal) => {
    return {
        info: (_a) => {
            var { footer, closeIconTooltip, okButtonProps, cancelButtonProps, okText = 'Принять', cancelText = 'Отмена', closable = true, content } = _a, props = __rest(_a, ["footer", "closeIconTooltip", "okButtonProps", "cancelButtonProps", "okText", "cancelText", "closable", "content"]);
            const instance = modal.info(Object.assign(Object.assign({}, props), { content: (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$flexDirection": 'column', "$gap": 8 }, content)), footer: (React.createElement(Footer, { footer: footer, okButtonProps: okButtonProps, cancelButtonProps: cancelButtonProps, okText: okText, cancelText: cancelText, onOk: props.onOk, onCancel: props.onCancel, destroy: () => {
                        instance.destroy();
                    } })), closeIcon: React.createElement(CloseIcon, Object.assign({}, closeIconTooltip)), closable }));
            return instance;
        },
        success: (_a) => {
            var { footer, closeIconTooltip, okButtonProps, cancelButtonProps, okText = 'Принять', cancelText = 'Отмена', closable = true, content } = _a, props = __rest(_a, ["footer", "closeIconTooltip", "okButtonProps", "cancelButtonProps", "okText", "cancelText", "closable", "content"]);
            const instance = modal.success(Object.assign(Object.assign({}, props), { content: (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$flexDirection": 'column', "$gap": 8 }, content)), footer: (React.createElement(Footer, { footer: footer, okButtonProps: okButtonProps, cancelButtonProps: cancelButtonProps, okText: okText, cancelText: cancelText, onOk: props.onOk, onCancel: props.onCancel, destroy: () => {
                        instance.destroy();
                    } })), closeIcon: React.createElement(CloseIcon, Object.assign({}, closeIconTooltip)), closable }));
            return instance;
        },
        warning: (_a) => {
            var { footer, closeIconTooltip, okButtonProps, cancelButtonProps, okText = 'Принять', cancelText = 'Отмена', closable = true, content } = _a, props = __rest(_a, ["footer", "closeIconTooltip", "okButtonProps", "cancelButtonProps", "okText", "cancelText", "closable", "content"]);
            const instance = modal.warning(Object.assign(Object.assign({}, props), { content: (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$flexDirection": 'column', "$gap": 8 }, content)), footer: (React.createElement(Footer, { footer: footer, okButtonProps: okButtonProps, cancelButtonProps: cancelButtonProps, okText: okText, cancelText: cancelText, onOk: props.onOk, onCancel: props.onCancel, destroy: () => {
                        instance.destroy();
                    } })), closeIcon: React.createElement(CloseIcon, Object.assign({}, closeIconTooltip)), closable }));
            return instance;
        },
        error: (_a) => {
            var { footer, closeIconTooltip, okButtonProps, cancelButtonProps, okText = 'Принять', cancelText = 'Отмена', closable = true, content } = _a, props = __rest(_a, ["footer", "closeIconTooltip", "okButtonProps", "cancelButtonProps", "okText", "cancelText", "closable", "content"]);
            const instance = modal.error(Object.assign(Object.assign({}, props), { content: (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$flexDirection": 'column', "$gap": 8 }, content)), footer: (React.createElement(Footer, { footer: footer, okButtonProps: okButtonProps, cancelButtonProps: Object.assign({ danger: true }, cancelButtonProps), okText: okText, cancelText: cancelText, onOk: props.onOk, onCancel: props.onCancel, destroy: () => {
                        instance.destroy();
                    } })), closeIcon: React.createElement(CloseIcon, Object.assign({}, closeIconTooltip)), closable }));
            return instance;
        },
        confirm: (_a) => {
            var { footer, closeIconTooltip, okButtonProps, cancelButtonProps, okText = 'Принять', cancelText = 'Отмена', image = { layout: 'contain' }, closable = true, content, className } = _a, props = __rest(_a, ["footer", "closeIconTooltip", "okButtonProps", "cancelButtonProps", "okText", "cancelText", "image", "closable", "content", "className"]);
            const hasImage = !!image.src;
            const imageDefaultLayout = image.layout || 'contain';
            const isCoverLayout = imageDefaultLayout === 'cover';
            const classNames = [className];
            if (isCoverLayout)
                classNames.push('tend-ui-modal-confirm-image-cover');
            const instance = modal.confirm(Object.assign(Object.assign({}, props), { closable, className: classNames.filter(Boolean).join(' '), content: (React.createElement(Box, { "$display": 'flex', "$flexDirection": 'column', "$gap": 8 }, content)), footer: (React.createElement(Footer, { padding: isCoverLayout ? '0 32px 24px' : undefined, footer: footer, okButtonProps: okButtonProps, cancelButtonProps: cancelButtonProps, okText: okText, cancelText: cancelText, onOk: props.onOk, onCancel: props.onCancel, destroy: () => {
                        instance.destroy();
                    } })), closeIcon: React.createElement(CloseIcon, Object.assign({}, closeIconTooltip)), icon: hasImage ? (React.createElement(ImageContainer, { "$layout": imageDefaultLayout },
                    React.createElement(Img, { src: image.src }))) : null }));
            return instance;
        },
    };
};
const Dialog = Object.assign({
    Styles,
    useDialog: () => {
        const [methods, holder] = AntModal.useModal();
        return [methodsFactory(methods), holder];
    },
    destroyAll: AntModal.destroyAll,
    config: AntModal.config,
}, methodsFactory(AntModal));

export { Dialog };
//# sourceMappingURL=Dialog.js.map
